import type { ApolloError } from 'apollo-client';
import { useContext, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { isUnauthorizedError } from '@confluence/error-boundary';
import {
	END_OF_TRIAL_MODAL_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { markErrorAsHandled } from '@confluence/graphql';
import { containsNoNetworkError, isGraphQLError } from '@confluence/network';
import { useSessionData } from '@confluence/session-data';

import { analytics } from '../experimentConstants';
import { useOperationalInfo, useOperationalInfoActions } from '../store/operationalStore';

export const COORDINATION = 'useLoadModalCoordinationToStore';
export const PAY = 'useLoadUserCanPayAndUpgradeToStore';
export const OSB = 'useGetOSBLink';
export const INSIGHTS = 'useCheckPageInsights';
export const RESTRICTIONS = 'useCheckPageRestrictions';
export const USERS = 'useGetNumberOfUsers';
export const GUEST = 'useCheckGuestAccess';
export const TIME = 'useLoadTrialInfoToStore';

export const useHandleHookStates = ({
	loading,
	error,
	hook,
}: {
	loading: boolean;
	error?: Error | ApolloError | null;
	hook: string;
}) => {
	const { setHooksLoading, setLoading, setError } = useOperationalInfoActions();
	const { hooksLoading } = useOperationalInfo();
	const { edition } = useSessionData();

	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		void setHooksLoading(hook, loading);
	}, [hook, loading, setHooksLoading]);

	useEffect(() => {
		const isLoaded = [...Object.values(hooksLoading)];
		if (
			isLoaded.length === Object.keys(hooksLoading).length &&
			isLoaded.every((hook) => hook === false)
		) {
			void setLoading(false);
		}
	}, [setLoading, hooksLoading]);

	useEffect(() => {
		if (error) {
			if (isUnauthorizedError(error) || containsNoNetworkError(error) || isGraphQLError(error)) {
				markErrorAsHandled(error);
				experienceTracker.abort({
					name: END_OF_TRIAL_MODAL_EXPERIENCE,
					reason: 'Expected GQL error',
					attributes: {
						edition,
					},
				});
			} else {
				experienceTracker.stopOnError({
					name: END_OF_TRIAL_MODAL_EXPERIENCE,
					error,
					attributes: {
						edition,
					},
				});
			}
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'error',
					actionSubject: analytics.HOOK_ACTION_SUBJECT_ID,
					source: analytics.SOURCE,
					attributes: {
						error: error.message,
						edition,
					},
				},
			}).fire();
			void setError(error);
			void setLoading(false);
		}
	}, [error, setError, setLoading, createAnalyticsEvent, experienceTracker, edition]);
};
